import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  History, Search, Filter, User, Calendar, Activity,
  FileText, Trash2, Edit, Key, UserPlus, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '@/lib/api';

const actionIcons = {
  create: <UserPlus className="w-4 h-4 text-green-500" />,
  update: <Edit className="w-4 h-4 text-blue-500" />,
  delete: <Trash2 className="w-4 h-4 text-red-500" />,
  reset_password: <Key className="w-4 h-4 text-yellow-500" />,
  toggle_active: <Settings className="w-4 h-4 text-purple-500" />,
  update_profile: <User className="w-4 h-4 text-blue-500" />,
};

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionTypes, setActionTypes] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    limit: 50,
    skip: 0
  });

  useEffect(() => {
    loadLogs();
    loadActionTypes();
  }, [filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      params.append('limit', filters.limit);
      params.append('skip', filters.skip);
      
      const res = await api.get(`/admin/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Super admin access required');
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadActionTypes = async () => {
    try {
      const res = await api.get('/admin/audit-logs/actions');
      setActionTypes(res.data.actions || []);
      setResourceTypes(res.data.resource_types || []);
    } catch (error) {
      console.error('Failed to load action types');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return '-';
    
    return Object.entries(details)
      .filter(([key]) => key !== 'password')
      .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
      .join(', ')
      .substring(0, 100) + (JSON.stringify(details).length > 100 ? '...' : '');
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <History className="w-6 h-6" />
          Audit Logs
        </h1>
        <p className="text-muted-foreground">Track all admin actions and changes</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select
              value={filters.action || "all"}
              onValueChange={(value) => setFilters({ ...filters, action: value === "all" ? "" : value, skip: 0 })}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map((action) => (
                  <SelectItem key={action} value={action}>
                    {formatAction(action)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={filters.resource_type || "all"}
              onValueChange={(value) => setFilters({ ...filters, resource_type: value === "all" ? "" : value, skip: 0 })}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-resource">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {resourceTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="ml-auto text-sm text-muted-foreground">
              Showing {logs.length} of {total} logs
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDate(log.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4" />
                          </div>
                          <span className="text-sm">{log.admin_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {actionIcons[log.action] || <Activity className="w-4 h-4" />}
                          <Badge variant="outline">
                            {formatAction(log.action)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {log.resource_type?.replace(/_/g, ' ')}
                        </Badge>
                        {log.resource_id && (
                          <span className="text-xs text-muted-foreground ml-2">
                            #{log.resource_id.substring(0, 8)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDetails(log.details)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground font-mono">
                          {log.ip_address || '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {total > filters.limit && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="outline"
                    disabled={filters.skip === 0}
                    onClick={() => setFilters({ ...filters, skip: Math.max(0, filters.skip - filters.limit) })}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {Math.floor(filters.skip / filters.limit) + 1} of {Math.ceil(total / filters.limit)}
                  </span>
                  <Button
                    variant="outline"
                    disabled={filters.skip + filters.limit >= total}
                    onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;
